import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { MODULE_PAGE_CSS } from './moduleBannerStyles';

const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const TYPES = ['Viewing', 'Inspection', 'Check-In', 'Check-Out', 'Survey', 'Maintenance Access', 'Other'];
const TYPE_ICONS: Record<string, string> = { Viewing: '🏠', Inspection: '🔍', 'Check-In': '🔑', 'Check-Out': '📤', Survey: '📐', 'Maintenance Access': '🔧', Other: '📅' };
const VIEW_FILTERS = ['All', 'Upcoming', 'Today', 'This week', 'Past', 'Viewings only', 'Needs outcome'] as const;
const OUTCOMES = ['Let', 'Interested', 'Not Interested', 'No Show', 'Completed'] as const;

const isPast = (v: any) => new Date(v.date_time) < new Date();
const isToday = (v: any) => new Date(v.date_time).toDateString() === new Date().toDateString();
const isThisWeek = (v: any) => {
  const d = new Date(v.date_time);
  const now = new Date();
  const end = new Date(now); end.setDate(end.getDate() + 7);
  return d >= now && d <= end;
};
const typeVal = (v: any) => v.type || v.viewing_type || 'Viewing';
const needsOutcome = (v: any) => isPast(v) && !v.outcome && v.status !== 'cancelled';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');
.vw-wrap * { box-sizing: border-box; }
.vw-wrap { font-family: 'Bricolage Grotesque', system-ui, sans-serif; }
.toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 20px; }
.search-box { display: flex; align-items: center; gap: 8px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 14px; flex: 1; min-width: 180px; max-width: 260px; }
.search-box input { border: none; outline: none; font-size: 13px; font-family: inherit; color: #0f172a; background: transparent; width: 100%; }
.vw-filter { padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; font-size: 13px; font-weight: 700; color: #012169; appearance: none; min-width: 180px; cursor: pointer; font-family: inherit; }
.vw-alert-slim { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 12px 16px; border-radius: 12px; margin-bottom: 14px; font-size: 13px; font-weight: 700; }
.vw-alert-slim.teal { background: #f0fdfa; border: 1.5px solid #99f6e4; color: #0f766e; }
.vw-alert-slim.amber { background: #fffbeb; border: 1.5px solid #fde68a; color: #92400e; }
.result-info { font-size: 12px; color: #94a3b8; font-weight: 600; margin-left: auto; }
.vw-card { background: #fff; border: 1.5px solid #e9ecef; border-left: 4px solid #0d9488; border-radius: 14px; padding: 16px 18px; margin-bottom: 10px; display: flex; gap: 16px; align-items: flex-start; flex-wrap: wrap; }
.vw-card.past { border-left-color: #cbd5e1; opacity: 0.92; background: #fafbfc; }
.vw-card.today { border-left-color: #1d4ed8; box-shadow: 0 4px 14px rgba(29,78,216,.08); }
.vw-icon { font-size: 28px; min-width: 36px; text-align: center; }
.vw-main { flex: 1; min-width: 220px; }
.vw-title { font-weight: 800; font-size: 15px; color: #0f172a; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vw-meta { font-size: 13px; color: #64748b; margin-top: 4px; }
.vw-notes { font-size: 12px; color: #94a3b8; margin-top: 6px; font-style: italic; }
.vw-side { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.vw-time { font-size: 13px; font-weight: 700; color: #374151; text-align: right; font-family: 'DM Mono', monospace; }
.status-pill { font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 6px; text-transform: uppercase; }
.btn-act { padding: 6px 12px; border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; border: 1.5px solid; font-family: inherit; white-space: nowrap; }
.actions-row { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.outcome-row { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 8px; }
.outcome-btn { padding: 4px 10px; border-radius: 7px; font-size: 11px; font-weight: 700; cursor: pointer; border: 1px solid #e2e8f0; background: #fff; color: #64748b; font-family: inherit; }
.outcome-btn:hover { border-color: #0d9488; color: #0d9488; }
.vw-panel { background: #fff; border: 1.5px solid #0d9488; border-radius: 16px; padding: 24px; margin-bottom: 24px; }
.vw-panel-title { font-size: 16px; font-weight: 800; color: #0d9488; margin: 0 0 18px; }
.vw-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.vw-grid .span2 { grid-column: span 2; }
.field-label { display: block; font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 5px; }
.field-input { width: 100%; padding: 9px 12px; border: 1.5px solid #d1d5db; border-radius: 9px; font-size: 14px; font-family: inherit; color: #0f172a; background: #fff; }
.field-input:focus { border-color: #0d9488; outline: none; }
.panel-actions { display: flex; gap: 10px; margin-top: 18px; }
.btn-primary { background: #0d9488; color: #fff; border: none; border-radius: 9px; padding: 10px 24px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: inherit; }
.btn-secondary { background: #fff; color: #374151; border: 1.5px solid #d1d5db; border-radius: 9px; padding: 10px 20px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; }
.toast-ok { background: #f0fdf4; color: #16a34a; border: 1.5px solid #bbf7d0; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
.toast-err { background: #fff1f1; color: #dc2626; border: 1.5px solid #fecaca; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
.vw-empty { text-align: center; padding: 64px 32px; background: #fff; border: 2px dashed #e2e8f0; border-radius: 18px; }
@media (max-width: 700px) { .vw-grid .span2 { grid-column: span 1; } }
`;

const BLANK = { property_address: '', visitor_name: '', email: '', phone: '', date_time: '', viewing_type: 'Viewing', notes: '' };

const STATUS_META: Record<string, { bg: string; color: string }> = {
  scheduled: { bg: '#eff6ff', color: '#1d4ed8' },
  confirmed: { bg: '#f0fdf4', color: '#16a34a' },
  completed: { bg: '#f1f5f9', color: '#64748b' },
  cancelled: { bg: '#fee2e2', color: '#dc2626' },
};
const OUTCOME_META: Record<string, { bg: string; color: string }> = {
  Let: { bg: '#dcfce7', color: '#16a34a' },
  Interested: { bg: '#dbeafe', color: '#1d4ed8' },
  'Not Interested': { bg: '#f1f5f9', color: '#64748b' },
  'No Show': { bg: '#fee2e2', color: '#dc2626' },
  Completed: { bg: '#f0fdf4', color: '#0d9488' },
};

export default function Viewings() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [viewings, setViewings] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ ...BLANK });
  const [filterTab, setFilterTab] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [showToday, setShowToday] = useState(false);
  const [showNeedsOutcome, setShowNeedsOutcome] = useState(false);

  const load = useCallback(async () => {
    if (!token) return; setLoading(true);
    const [v, p] = await Promise.all([
      fetch(`${API_URL}/landlord/viewings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/landlord/properties`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]);
    setViewings(v.viewings || []);
    setProperties(p.properties || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (force = false) => {
    if (!form.property_address || !form.date_time) { setError('Property and date/time are required'); return; }
    setSaving(true); setError('');
    const r = await fetch(`${API_URL}/landlord/viewings`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...form, force }),
    });
    const d = await r.json();
    if (r.status === 409 && !force) {
      if (window.confirm(`${d.error}. Book anyway?`)) { setSaving(false); return save(true); }
      setError(d.error); setSaving(false); return;
    }
    if (!r.ok) { setError(d.error || 'Failed to save'); setSaving(false); return; }
    setSuccess('Appointment booked'); setShowAdd(false); setForm({ ...BLANK });
    load(); setSaving(false); setTimeout(() => setSuccess(''), 3000);
  };

  const setOutcome = async (id: number, outcome: string) => {
    const r = await fetch(`${API_URL}/landlord/viewings/${id}/outcome`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ outcome }),
    });
    if (!r.ok) { const d = await r.json(); setError(d.error || 'Failed'); return; }
    const updated = await r.json();
    if (outcome === 'Let') quickLet(updated.viewing);
    load();
  };

  const openInventory = (v: any) => {
    navigate('/landlord/inventory', { state: { prefillProperty: v.property_address, openAdd: true } });
  };

  const quickLet = (v: any) => {
    navigate('/landlord/tenants/new', {
      state: {
        quickLet: {
          full_name: v.visitor_name !== '—' ? v.visitor_name : '',
          email: v.visitor_email || v.email || '',
          phone: v.visitor_phone || v.phone || '',
          property_address: v.property_address,
        },
      },
    });
  };

  const startEdit = (v: any) => {
    setEditForm({
      property_address: v.property_address || '',
      visitor_name: v.visitor_name || '',
      email: v.visitor_email || '',
      phone: v.visitor_phone || '',
      date_time: v.date_time ? new Date(v.date_time).toISOString().slice(0, 16) : '',
      viewing_type: typeVal(v),
      notes: v.notes || '',
      outcome: v.outcome || '',
      status: v.status || 'scheduled',
    });
    setEditingId(v.id); setShowAdd(false);
  };

  const saveEdit = async () => {
    if (!editForm.date_time) { setError('Date/time is required'); return; }
    setSaving(true); setError('');
    const r = await fetch(`${API_URL}/landlord/viewings/${editingId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(editForm),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || 'Failed'); setSaving(false); return; }
    setSuccess('Updated'); setEditingId(null); load(); setSaving(false);
    setTimeout(() => setSuccess(''), 3000);
  };

  const deleteViewing = async (id: number) => {
    if (!window.confirm('Delete this appointment?')) return;
    await fetch(`${API_URL}/landlord/viewings/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const cancelViewing = async (v: any) => {
    if (!window.confirm('Cancel this appointment? It will stay on record as cancelled.')) return;
    const r = await fetch(`${API_URL}/landlord/viewings/${v.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        status: 'cancelled',
        date_time: v.date_time,
        property_address: v.property_address,
        visitor_name: v.visitor_name,
        visitor_email: v.visitor_email || v.email,
        visitor_phone: v.visitor_phone || v.phone,
        viewing_type: typeVal(v),
        notes: v.notes,
      }),
    });
    if (!r.ok) { const d = await r.json(); setError(d.error || 'Failed to cancel'); return; }
    setSuccess('Appointment cancelled');
    load();
    setTimeout(() => setSuccess(''), 3000);
  };

  const confirmViewing = async (id: number) => {
    const r = await fetch(`${API_URL}/landlord/viewings/${id}/confirm`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    showToast(r.ok ? `Confirmation sent to ${d.sent_to}` : d.error || 'Failed');
    if (r.ok) load();
  };

  const downloadIcs = async (id: number) => {
    const r = await fetch(`${API_URL}/landlord/viewings/${id}/ics`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `viewing-${id}.ics`; a.click();
    URL.revokeObjectURL(url);
  };

  const upcoming = viewings.filter(v => !isPast(v) && v.status !== 'cancelled');
  const todayList = viewings.filter(v => isToday(v) && v.status !== 'cancelled');
  const weekList = viewings.filter(v => isThisWeek(v) && v.status !== 'cancelled');
  const needOutcomeList = viewings.filter(needsOutcome);

  const counts = {
    upcoming: upcoming.length,
    today: todayList.length,
    week: weekList.length,
    needOutcome: needOutcomeList.length,
  };

  const filterCount = (f: string) => {
    if (f === 'Upcoming') return counts.upcoming;
    if (f === 'Today') return counts.today;
    if (f === 'This week') return counts.week;
    if (f === 'Past') return viewings.filter(isPast).length;
    if (f === 'Viewings only') return viewings.filter(v => typeVal(v) === 'Viewing').length;
    if (f === 'Needs outcome') return counts.needOutcome;
    return 0;
  };

  const displayed = [...viewings]
    .sort((a, b) => new Date(a.date_time).getTime() - new Date(b.date_time).getTime())
    .filter(v => {
      if (filterTab === 'Upcoming' && (isPast(v) || v.status === 'cancelled')) return false;
      if (filterTab === 'Today' && !isToday(v)) return false;
      if (filterTab === 'This week' && !isThisWeek(v)) return false;
      if (filterTab === 'Past' && !isPast(v)) return false;
      if (filterTab === 'Viewings only' && typeVal(v) !== 'Viewing') return false;
      if (filterTab === 'Needs outcome' && !needsOutcome(v)) return false;
      const q = search.toLowerCase();
      if (!q) return true;
      return typeVal(v).toLowerCase().includes(q) || v.property_address.toLowerCase().includes(q)
        || (v.visitor_name || '').toLowerCase().includes(q);
    });

  return (
    <div className="mod-page vw-wrap">
      <style>{MODULE_PAGE_CSS}{CSS}</style>

      <div className="mod-banner-wrap mod-banner-viewings">
        <div className="mod-banner-glass">
          <div>
            <h1>📅 Viewings</h1>
            <p>{counts.upcoming} upcoming · {counts.today} today · {counts.needOutcome} need outcome</p>
          </div>
          <button type="button" className="mod-btn-white" onClick={() => { setShowAdd(true); setEditingId(null); setError(''); }}>+ Book appointment</button>
        </div>
      </div>

      <div className="mod-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="mod-kpi mod-kpi-teal">
          <div className="kl">Upcoming</div>
          <div className="kv">{counts.upcoming}</div>
          <div className="ks">scheduled</div>
        </div>
        <div className="mod-kpi mod-kpi-blue">
          <div className="kl">Today</div>
          <div className="kv">{counts.today}</div>
          <div className="ks">{counts.today > 0 ? 'get keys ready' : 'clear diary'}</div>
        </div>
        <div className="mod-kpi mod-kpi-green">
          <div className="kl">This week</div>
          <div className="kv">{counts.week}</div>
          <div className="ks">next 7 days</div>
        </div>
        <div className="mod-kpi mod-kpi-amber">
          <div className="kl">Needs outcome</div>
          <div className="kv">{counts.needOutcome}</div>
          <div className="ks">{counts.needOutcome > 0 ? 'record result' : 'all done'}</div>
        </div>
      </div>

      {todayList.length > 0 && !showToday && (
        <div className="vw-alert-slim teal">
          <span>📅 {todayList.length} appointment{todayList.length > 1 ? 's' : ''} today</span>
          <button type="button" className="btn-act" style={{ background: '#0d9488', color: '#fff', borderColor: '#0d9488' }}
            onClick={() => { setShowToday(true); setFilterTab('Today'); }}>View</button>
        </div>
      )}

      {needOutcomeList.length > 0 && !showNeedsOutcome && (
        <div className="vw-alert-slim amber">
          <span>✏️ {needOutcomeList.length} past appointment{needOutcomeList.length > 1 ? 's' : ''} need outcome</span>
          <button type="button" className="btn-act" style={{ background: '#d97706', color: '#fff', borderColor: '#d97706' }}
            onClick={() => { setShowNeedsOutcome(true); setFilterTab('Needs outcome'); }}>View</button>
        </div>
      )}

      {error && <div className="toast-err">❌ {error}</div>}
      {success && <div className="toast-ok">✅ {success}</div>}

      {showAdd && (
        <div className="vw-panel">
          <div className="vw-panel-title">+ Book Appointment</div>
          <div className="vw-grid">
            <div>
              <label className="field-label">Property *</label>
              <select className="field-input" value={form.property_address} onChange={e => setForm(p => ({ ...p, property_address: e.target.value }))}>
                <option value="">— Select property —</option>
                {properties.map((p: any) => <option key={p.id} value={p.address}>{p.address}, {p.postcode}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Type *</label>
              <select className="field-input" value={form.viewing_type} onChange={e => setForm(p => ({ ...p, viewing_type: e.target.value }))}>
                {TYPES.map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {t}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Date &amp; Time *</label>
              <input type="datetime-local" className="field-input" value={form.date_time} onChange={e => setForm(p => ({ ...p, date_time: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Visitor name</label>
              <input className="field-input" value={form.visitor_name} onChange={e => setForm(p => ({ ...p, visitor_name: e.target.value }))} placeholder="John Smith" />
            </div>
            <div>
              <label className="field-label">Email</label>
              <input type="email" className="field-input" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Mobile</label>
              <input className="field-input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="span2">
              <label className="field-label">Notes</label>
              <input className="field-input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Keys with agent, parking at rear…" />
            </div>
          </div>
          <div className="panel-actions">
            <button onClick={() => save()} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Book'}</button>
            <button onClick={() => { setShowAdd(false); setError(''); }} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {editingId !== null && (
        <div className="vw-panel">
          <div className="vw-panel-title">✏️ Edit Appointment</div>
          <div className="vw-grid">
            <div>
              <label className="field-label">Type</label>
              <select className="field-input" value={editForm.viewing_type} onChange={e => setEditForm((p: any) => ({ ...p, viewing_type: e.target.value }))}>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Date &amp; Time *</label>
              <input type="datetime-local" className="field-input" value={editForm.date_time} onChange={e => setEditForm((p: any) => ({ ...p, date_time: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Visitor</label>
              <input className="field-input" value={editForm.visitor_name} onChange={e => setEditForm((p: any) => ({ ...p, visitor_name: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Email</label>
              <input type="email" className="field-input" value={editForm.email} onChange={e => setEditForm((p: any) => ({ ...p, email: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Outcome</label>
              <select className="field-input" value={editForm.outcome || ''} onChange={e => setEditForm((p: any) => ({ ...p, outcome: e.target.value }))}>
                <option value="">— Not recorded —</option>
                {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="span2">
              <label className="field-label">Notes</label>
              <input className="field-input" value={editForm.notes || ''} onChange={e => setEditForm((p: any) => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <div className="panel-actions">
            <button onClick={saveEdit} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setEditingId(null)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {!loading && viewings.length > 0 && (
        <div className="toolbar">
          <div className="search-box">
            <span style={{ color: '#94a3b8' }}>🔍</span>
            <input placeholder="Search property, visitor, type…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="vw-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
            {VIEW_FILTERS.map(f => (
              <option key={f} value={f}>{f}{f !== 'All' && filterCount(f) > 0 ? ` (${filterCount(f)})` : ''}</option>
            ))}
          </select>
          <span className="result-info">{displayed.length} shown</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading appointments…</div>
      ) : viewings.length === 0 ? (
        <div className="vw-empty">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
          <h3 style={{ margin: '0 0 8px' }}>No appointments yet</h3>
          <p style={{ color: '#94a3b8' }}>Book viewings, inspections, check-ins and check-outs</p>
          <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => setShowAdd(true)}>+ Book first appointment</button>
        </div>
      ) : displayed.length === 0 ? (
        <div className="vw-empty">
          <h3>No matches</h3>
          <button className="btn-secondary" onClick={() => { setFilterTab('All'); setSearch(''); }}>Clear filters</button>
        </div>
      ) : (
        displayed.map(v => {
          const tv = typeVal(v);
          const past = isPast(v);
          const sm = STATUS_META[v.status || 'scheduled'] || STATUS_META.scheduled;
          const om = v.outcome ? OUTCOME_META[v.outcome] : null;
          return (
            <div key={v.id} className={`vw-card${past ? ' past' : ''}${isToday(v) ? ' today' : ''}`}>
              <div className="vw-icon">{TYPE_ICONS[tv] || '📅'}</div>
              <div className="vw-main">
                <div className="vw-title">
                  {tv}
                  <span className="status-pill" style={{ background: sm.bg, color: sm.color }}>{v.status || 'scheduled'}</span>
                  {om && <span className="status-pill" style={{ background: om.bg, color: om.color }}>{v.outcome}</span>}
                </div>
                <div className="vw-meta">{v.property_address}</div>
                {v.visitor_name && v.visitor_name !== '—' && (
                  <div className="vw-meta">👤 {v.visitor_name}
                    {(v.visitor_email || v.email) && ` · ${v.visitor_email || v.email}`}
                    {(v.visitor_phone || v.phone) && ` · ${v.visitor_phone || v.phone}`}
                  </div>
                )}
                {v.notes && <div className="vw-notes">{v.notes}</div>}
                {(past || isToday(v)) && !v.outcome && (
                  <div className="outcome-row">
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', alignSelf: 'center' }}>Outcome:</span>
                    {OUTCOMES.map(o => (
                      <button key={o} type="button" className="outcome-btn" onClick={() => setOutcome(v.id, o)}>{o}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="vw-side">
                <div className="vw-time">{fmt(v.date_time)}</div>
                <div className="actions-row">
                  {(v.visitor_email || v.email) && !past && (
                    <button className="btn-act" style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }} onClick={() => confirmViewing(v.id)}>📧 Confirm</button>
                  )}
                  <button className="btn-act" style={{ background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' }} onClick={() => downloadIcs(v.id)}>📅 ICS</button>
                  {(tv === 'Check-In' || tv === 'Check-Out') && (
                    <button className="btn-act" style={{ background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }} onClick={() => openInventory(v)}>📦 Inventory</button>
                  )}
                  {v.outcome === 'Let' && (
                    <button className="btn-act" style={{ background: '#ede9fe', color: '#6d28d9', borderColor: '#c4b5fd' }} onClick={() => quickLet(v)}>⚡ Quick Let</button>
                  )}
                  <button className="btn-act" style={{ background: '#f0f9ff', color: '#1d4ed8', borderColor: '#93c5fd' }} onClick={() => startEdit(v)}>✏️</button>
                  {v.status !== 'cancelled' && !past && (
                    <button className="btn-act" style={{ background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }} onClick={() => cancelViewing(v)}>✖️ Cancel</button>
                  )}
                  <button className="btn-act" style={{ background: '#fff', color: '#dc2626', borderColor: '#fecaca' }} onClick={() => deleteViewing(v.id)}>🗑️</button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
